import * as React from "react";
import { useEffect } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import PropTypes from 'prop-types';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Toolbar from '@mui/material/Toolbar';
import Paper from '@mui/material/Paper';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import { visuallyHidden } from '@mui/utils';
import {getDatabase, ref, query, get, orderByKey, limitToLast, endAt} from 'firebase/database'
import Button from '@mui/material/Button';
import CancelIcon from '@mui/icons-material/Cancel';
import MailIcon from '@mui/icons-material/Mail'
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { getAuth } from "firebase/auth";
import { app } from '../components/Firebase';
import { Hidden } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import { appCheck } from "../App";
import { getToken  } from "firebase/app-check";

function createRow(name, email, surveyId, date, status, key) {
  return {
    name,
    email,
    surveyId,
    date,
    status,
    key
  };
}

function fetchSurveyStatus(dbRef, num, setRows, lastKey, setLastKey, isFetching) {
  var orderedConstraint = orderByKey(dbRef);
  var q;
  if (lastKey) {
    q = query(dbRef, orderedConstraint, endAt(lastKey), limitToLast(num+1));
  } else {
    q = query(dbRef, orderedConstraint, limitToLast(num+1));
  }
  get(q).then((snapshot) => {
    var newRows=[];
      var data = [];
      snapshot.forEach(function(childSnapshot) {
          var item = childSnapshot.val();
          item.key = childSnapshot.key;
          // Skip the first item if it's already included in the previous set
          if (item.key !== lastKey) {
              data.push(item);
          }
      });
      if (data.length > 0) {
          // Update the last key
          setLastKey(data[0].key);
          // Reverse the data to get latest entries first
          data.reverse();
          data.forEach(function(item) {
            let localDate = new Date(item.dateRequested).toLocaleDateString();
            //let localDateRequested = localDate.getFullYear() + '/' + (localDate.getMonth() + 1) + '/' + localDate.getDate();
            newRows.push(createRow(item.surveyTakerName, item.emailAddress, item.surveyId, localDate, item.status, item.key));
          });
      } else {
          console.log('No more surveys');
      }
      setRows(prevRows => [...prevRows, ...newRows]);
  }).catch((error) => {
      console.error(error);
  }).finally(() => {
    isFetching.current = false;
});
}

const headCells = [
    {
      id: 'name',
      numeric: false,
      disablePadding: true,
      label: 'Name',
    },
    {
      id: 'email',
      numeric: false,
      disablePadding: false,
      label: 'Email',
    },
    {
      id: 'type',
      numeric: false,
      disablePadding: false,
      label: 'Assessment Type',
    },
    {
      id: 'date',
      numeric: false,
      disablePadding: false,
      label: 'Date Requested',
    },
    {
      id: 'status',
      numeric: false,
      disablePadding: false,
      label: 'Status',
    },
    {
      id: 'action',
      numeric: false,
      disablePadding: false,
      label: 'Action',
    },
  ];

// Since 2020 all major browsers ensure sort stability with Array.prototype.sort().
// stableSort() brings sort stability to non-modern browsers (notably IE11). If you
// only support modern browsers you can replace stableSort(exampleArray, exampleComparator)
// with exampleArray.slice().sort(exampleComparator)
function stableSort(array, comparator) {
const stabilizedThis = array.map((el, index) => [el, index]);
stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
    return order;
    }
    return a[1] - b[1];
});
return stabilizedThis.map((el) => el[0]);
}

function descendingComparator(a, b, orderBy) {
  if (orderBy === 'date') { // replace 'date' with your actual date field
    const dateA = new Date(a[orderBy]);
    const dateB = new Date(b[orderBy]);
    if (dateB < dateA) {
      return -1;
    }
    if (dateB > dateA) {
      return 1;
    }
    return 0;
  } else {
    if (b[orderBy] < a[orderBy]) {
      return -1;
    }
    if (b[orderBy] > a[orderBy]) {
      return 1;
    }
    return 0;
  }
}


function getComparator(order, orderBy) {
return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function SurveyStatusTableHead(props) {
    const { order, orderBy, onRequestSort } =
      props;
    const createSortHandler = (property) => (event) => {
      onRequestSort(event, property);
    };
    return (
      <TableHead sx={{background:"lightblue"}}>
        <TableRow>
          {headCells.map((headCell) => (
            <TableCell
              key={headCell.id}
              align={headCell.numeric ? 'right' : 'left'}
              padding={headCell.disablePadding ? 'none' : 'normal'}
              sortDirection={orderBy === headCell.id ? order : false}
            >
              <TableSortLabel
                active={orderBy === headCell.id}
                direction={orderBy === headCell.id ? order : 'asc'}
                onClick={createSortHandler(headCell.id)}
              >
                {headCell.label}
                {orderBy === headCell.id ? (
                  <Box component="span" sx={visuallyHidden}>
                    {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                  </Box>
                ) : null}
              </TableSortLabel>
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
    );
  }
  
  SurveyStatusTableHead.propTypes = {
    onRequestSort: PropTypes.func.isRequired,
    order: PropTypes.oneOf(['asc', 'desc']).isRequired,
    orderBy: PropTypes.string.isRequired,
    rowCount: PropTypes.number.isRequired,
  };
  
  function SurveyStatusTableToolbar() {
  
    return (
      <Toolbar
        sx={{
          pl: { sm: 2 },
          pr: { xs: 1, sm: 1 }
        }}
      >
          <Typography
            sx={{ flex: '1 1 100%' }}
            variant="h6"
            id="tableTitle"
            component="div"
          >
            Requested Surveys
          </Typography>
      </Toolbar>
    );
  }
  
  function SurveyStatusTable(props) {
    const { userUid } = props;
    const [order, setOrder] = React.useState('desc');
    const [orderBy, setOrderBy] = React.useState('date');
    const [page, setPage] = React.useState(0);
    const [dense, setDense] = React.useState(false);
    const [rowsPerPage, setRowsPerPage] = React.useState(5);
    const [rows, setRows] = React.useState([]);
    const [lastKey, setLastKey] = React.useState(null);
    const isFetching = React.useRef(false);
    const [cancellationFailureDialogOpen, setCancellationFailureDialogOpen] = React.useState(false);
    const [circleProg, setCircleProg] = React.useState(false);
    const maxRowsPerPage = 50;
    useEffect(() => {
      if (userUid){
        if(!isFetching.current)
        {
          isFetching.current = true;
          const database = getDatabase();
          const firebaseRef = ref(database,`SurveyStatus/${userUid}/`);
          fetchSurveyStatus(firebaseRef, maxRowsPerPage*2, setRows, lastKey, setLastKey, isFetching);
        }
      }
      else{
        setRows([]);
      }
    }, [userUid]); // Include userUid in the dependency array

    const handleCancellationFailureDialogOpen = () => {
      setCancellationFailureDialogOpen(true);
    };
  
    const handleCancellationFailureDialogClose = () => {
      setCancellationFailureDialogOpen(false);
    };  

    const handleRequestSort = (event, property) => {
      const isAsc = orderBy === property && order === 'asc';
      setOrder(isAsc ? 'desc' : 'asc');
      setOrderBy(property);
    };
  
    const handleChangePage = (event, newPage) => {
      if( newPage > page && (((page+1)*rowsPerPage + maxRowsPerPage) > rows.length) )
      {
        if (userUid) {
          if(!isFetching.current)
          {
            isFetching.current = true;
            const database = getDatabase();
            const firebaseRef = ref(database,`SurveyStatus/${userUid}/`);
            // Call fetchSurveyStatus() again to get the next maxRowsPerPage surveys, and so on...
            fetchSurveyStatus(firebaseRef, maxRowsPerPage, setRows, lastKey, setLastKey, isFetching); //stay "rowsPerPage" ahead of visible table
          }
        }
        else{
          setRows([]);
        }
  
      }
      setPage(newPage);
    };
  
    //sets back to start of table with new rows per page
    const handleChangeRowsPerPage = (event) => {
      setRowsPerPage(parseInt(event.target.value, 10));
      setPage(0);
      if (userUid) {
        if(!isFetching.current)
        {
          isFetching.current = true;
          const database = getDatabase();
          const firebaseRef = ref(database,`SurveyStatus/${userUid}/`);
          // Call fetchSurveyStatus() again to get the next 50 surveys, and so on...
          fetchSurveyStatus(firebaseRef, maxRowsPerPage, setRows, lastKey, setLastKey, isFetching); //stay "rowsPerPage" ahead of visible table
        }
      }
      else{
        setRows([]);
      }
    };
  
    const handleChangeDense = (event) => {
      setDense(event.target.checked);
    };

    async function handleSurveyCancellation(key) {
      if(circleProg)
      {
        return;
      }
      setCircleProg(true);
      const url = `/cancelSurveyRequest`;
      const data = { requestId: key };
    
      let retryCount = 0;
      let success = false;
      let error = null;
    
      while (retryCount < 3 && !success) {
        try {
          // Get the ID token of the current user.
          const idToken = await getAuth().currentUser.getIdToken();
          // Get a valid App Check token
          let appCheckTokenResponse;
          try {
            appCheckTokenResponse = await getToken(appCheck, false);
          } catch (err) {
            console.error("App Check token retrieval failed:", err);
            return; // Abort if unable to retrieve token
          }
          // Send a request to the Firebase Function
          const response = await fetch(url, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${idToken}`,
              'X-Firebase-AppCheck': appCheckTokenResponse.token
            },
            body: JSON.stringify(data),
          });
    
          const result = await response.json();
    
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
    
          // Read result of the Cloud Function.
          if (result.status === 'success') {
            // Remove the row from the state
            setRows(rows.filter(row => row.key !== key));
            success = true;
            setCircleProg(false);
            return;
          } else {
            // Launch a dialogue box
            handleCancellationFailureDialogOpen();
          }
    
        } catch (err) {
          error = err;
          console.warn(`Attempt ${retryCount} failed. Retrying...`);
        }
        retryCount++;
      }
    
      if (!success) {
        console.warn('All attempts failed:', error);
        // Launch a dialogue box
        handleCancellationFailureDialogOpen();
      }
      setCircleProg(false);
    }    

    async function handleReportResend(key) {
      const lastClick = parseInt(localStorage.getItem(`LastReportResend${key}`), 10); // Ensure correct data type
      const tenMinutesInMilliseconds = 600000;
      const now = Date.now();
      // Check if resend request is allowed
      if (isNaN(lastClick) || (now - lastClick > tenMinutesInMilliseconds)) {
        if (circleProg) {
          return;
        }
        localStorage.setItem(`LastReportResend${key}`, now.toString()); // Store timestamp as string
        setCircleProg(true);
        const url = `/resendReportRequest`;
        const data = { requestId: key };
        let success = false;
    
        try {
          // Get the ID token of the current user
          const idToken = await getAuth().currentUser.getIdToken();
          // Get a valid App Check token
          let appCheckTokenResponse;
          try {
            appCheckTokenResponse = await getToken(appCheck, false);
          } catch (err) {
            console.error("App Check token retrieval failed:", err);
            return; // Abort if unable to retrieve token
          }
          // Send a request to the Firebase Function
          const response = await fetch(url, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${idToken}`,
              'X-Firebase-AppCheck': appCheckTokenResponse.token
            },
            body: JSON.stringify(data),
          });
    
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
    
          const result = await response.json();
    
          if (result.status === 'success') {
            success = true;
            setTimeout(() => {
              alert('Report was successfully sent.');
            }, 10);
          }
        } catch (err) {
          console.error("Error during resend:", err);
        } finally {
          setCircleProg(false);
          if (!success) {
            // Revert timestamp on failure
            localStorage.setItem(`LastReportResend${key}`, lastClick?.toString() || '');
            setTimeout(() => {
              alert('Report failed to send.');
            }, 10);
          }
        }
      } else {
        setTimeout(() => {
          alert('You requested this report too recently.');
        }, 10);
      }
    }    

    // Avoid a layout jump when reaching the last page with empty rows.
    const emptyRows =
      page > 0 ? Math.max(0, (1 + page) * rowsPerPage - rows.length) : 0;
  
    const visibleRows = React.useMemo(
      () =>
        stableSort(rows, getComparator(order, orderBy)).slice(
          page * rowsPerPage,
          page * rowsPerPage + rowsPerPage,
        ),
      [order, orderBy, page, rowsPerPage, rows],
    );
  
    return (
      <Box sx={{ width: '100%' }}>
        <Paper sx={{ width: '100%', mb: 2 }}>
          <SurveyStatusTableToolbar />
          <Hidden xsUp={!circleProg}>
            <CircularProgress color="success" m={10}/>
          </Hidden>
          <TableContainer>
            <Dialog
              open={cancellationFailureDialogOpen}
              onClose={handleCancellationFailureDialogClose}
              aria-labelledby="alert-dialog-title"
              aria-describedby="alert-dialog-description"
            >
              <DialogTitle id="alert-dialog-title">
                {"Cancellation Failure"}
              </DialogTitle>
              <DialogContent>
                <DialogContentText id="alert-dialog-description">
                  The cancellation request has failed. Try refreshing the page.
                </DialogContentText>
              </DialogContent>
              <DialogActions>
                <Button onClick={handleCancellationFailureDialogClose} autoFocus>
                  Close
                </Button>
              </DialogActions>
            </Dialog>
            <Table
              sx={{ minWidth: 750 }}
              aria-labelledby="tableTitle"
              size={dense ? 'small' : 'medium'}
            >
              <SurveyStatusTableHead
                order={order}
                orderBy={orderBy}
                onRequestSort={handleRequestSort}
                rowCount={rows.length}
              />
              <TableBody>
                {visibleRows.map((row, index) => {
                  const labelId = `survey-status-table-checkbox-${index}`;
  
                  return (
                    <TableRow
                      hover
                      tabIndex={-1}
                      key={row.name}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell
                        component="th"
                        id={labelId}
                        scope="row"
                        padding="none"
                      >
                        {row.name}
                      </TableCell>
                      <TableCell align="left">{row.email}</TableCell>
                      <TableCell align="left">{row.surveyId}</TableCell>
                      <TableCell align="left">{row.date}</TableCell>
                      <TableCell align="left" style={{color: row.status === 'Completed' ? 'green' : row.status === 'Requested' ? 'black' : '#FF3300'}}>{row.status}</TableCell>
                      <TableCell align="left"> {
                      row.status === 'Requested' ? 
                        <Button onClick={() => handleSurveyCancellation(row.key)} color="secondary" startIcon={<CancelIcon />} variant="contained">Cancel</Button> 
                        : (
                          row.status === 'Completed' ? <Button onClick={() => handleReportResend(row.key)} color="secondary" startIcon={<MailIcon />} variant="contained">Resend Report</Button> : ""
                        )
                      } 
                      </TableCell>
                    </TableRow>
                  );
                })}
                {emptyRows > 0 && (
                  <TableRow
                    style={{
                      height: (dense ? 33 : 53) * emptyRows,
                    }}
                  >
                    <TableCell colSpan={6} />
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={rows.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>
        <FormControlLabel
          control={<Switch checked={dense} onChange={handleChangeDense} />}
          label="Dense padding"
        />
        <Typography>
          Dates are displayed in local time.
        </Typography>
      </Box>
    );
  }
  export default SurveyStatusTable;